import queryString from "query-string";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  getFieldDate,
  getFieldFormattedValue,
  getFieldName,
  getFieldText,
  updateEntity,
} from "../scripts/helperScripts";
import FosterHomeMembersEntity from "./FosterHomeMembersEntity";
import BackIcon from "./common/BackIcon";
import FieldDisplay from "./common/FieldDisplay";
import Input from "./common/Input";
import {
  getFosterHomeAddressesByID,
  getFosterHomeMembers,
  getProgramUnitsConfig,
  searchSOARFosterHome,
} from "./services/soar";
import Documents from "./sharepoint/Documents";

function FosterHome(props) {
  const location = useLocation();
  const qs = queryString.parse(location.search);
  const initialTab = location.state?.tab || qs.tab;
  const [home, setHome] = useState(location.state?.home || {});
  const [sageVendorId, setSageVendorId] = useState();
  const [editingSageVendorId, setEditingSageVendorId] = useState(false);
  const [addresses, setAddresses] = useState(location.state?.addresses || []);
  const [members, setMembers] = useState([]);
  const [updateMembers, setUpdateMembers] = useState(false);
  const [fosterHomeBedsLazy, setFosterHomeBedsLazy] = useState();
  const [fosterHomeRatesLazy, setFosterHomeRatesLazy] = useState();
  const [rulesInvestigationsLazy, setRulesInvestigationsLazy] = useState([]);
  const [correspondenceLazy, setCorrespondenceLazy] = useState();
  const [notesLazy, setNotesLazy] = useState();
  const isMountedRef = useRef(true);
  const [activeTab, setActiveTab] = useState("members");
  const params = useParams();
  const [id, setId] = useState(null);
  const navigate = useNavigate();
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  const [puConfig, setPuConfig] = useState({});

  //Route Access Protection
  useEffect(() => {
    if (safyInfo?.user?.modules) {
      //add passed id
      if (
        safyInfo.user.modules.includes("Foster Homes") === false &&
        !props.passedID
      ) {
        navigate("/");
      }
    } else navigate("/");
  });

  useEffect(() => {
    return () => (isMountedRef.current = false);
  }, []);

  const loadFosterHomeBedsEntity = useCallback(() => {
    import("./FosterHomeBedsEntity")
      .then((module) => module.default)
      .then((bedsEntity) =>
        setFosterHomeBedsLazy(
          React.createElement(bedsEntity, {
            fosterHome: home,
            addresses: addresses,
          })
        )
      );
  }, [home, addresses]);

  const loadFosterHomeRatesEntity = useCallback(() => {
    import("./FosterHomeRatesEntity")
      .then((module) => module.default)
      .then((ratesEntity) =>
        setFosterHomeRatesLazy(
          React.createElement(ratesEntity, {
            fosterHome: home,
            addresses: addresses,
          })
        )
      );
  }, [home, addresses]);

  const loadRulesInvestigations = useCallback(() => {
    import("./FPInvestigations")
      .then((module) => module.default)
      .then((investigationsComp) =>
        setRulesInvestigationsLazy(
          React.createElement(investigationsComp, {
            fosterHome: home,
            addresses: addresses,
          })
        )
      );
  }, [home, addresses]);

  const loadCorrespondence = useCallback(() => {
    import("./FosterHomeCorrespondence")
      .then((module) => module.default)
      .then((correspondenceComp) =>
        setCorrespondenceLazy(
          React.createElement(correspondenceComp, {
            fosterHome: home,
            addresses: addresses,
          })
        )
      );
  }, [home, addresses]);

  const loadFosterHomeNotes = useCallback(() => {
    import("./FosterHomeNotes")
      .then((module) => module.default)
      .then((notesComp) =>
        setNotesLazy(
          React.createElement(notesComp, {
            fosterHome: home,
            addresses: addresses,
          })
        )
      );
  }, [home, addresses]);

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
      switch (initialTab) {
        case "beds":
          loadFosterHomeBedsEntity();
          break;
        case "rates":
          loadFosterHomeRatesEntity();
          break;
        case "rules":
          loadRulesInvestigations();
          break;
        case "correspondence":
          loadCorrespondence();
          break;
        case "notes":
          loadFosterHomeNotes();
          break;
        default:
          break;
      }
    }
  }, [
    initialTab,
    loadFosterHomeBedsEntity,
    loadFosterHomeRatesEntity,
    loadRulesInvestigations,
    loadCorrespondence,
    loadFosterHomeNotes,
  ]);

  useEffect(() => {
    if (props.passedID) {
      setId(props.passedID);
    } else {
      setId(params.id);
    }
  }, [props.passedID, params.id]);

  useEffect(() => {
    if (Object.keys(home).length === 0 && id !== undefined && id !== null) {
      searchSOARFosterHome("Guid", id).then((response) => {
        const resp = JSON.parse(response);

        if (resp && resp.Homes && resp.Homes.Home && resp.Homes.Home[0]) {
          setHome(resp.Homes.Home[0]);
          getProgramUnitConfig(resp.Homes.Home[0].ccx_programunit["#text"]);
        } else {
          setHome({});
        }
      });
      // getDivisionActiveFosterHomes(divisions).then((response) => {
      //   if (isMountedRef.current) {
      //     if (response) {
      //       const homeResult = response.filter((r) =>
      //         r.ccx_fostercarehomeid.toUpperCase().includes(id.toUpperCase())
      //       )[0];

      //       setHome(homeResult || {});
      //     }
      //     setFosterHomeLoaded(true);
      //   }
      // });
    }
  }, [home, id]);

  useEffect(() => {
    if (home?.ccx_programunit?.["#text"]) {
      getProgramUnitConfig(home.ccx_programunit["#text"]);
    }
  }, [home?.ccx_programunit]);

  const getProgramUnitConfig = (programUnitId) => {
    getProgramUnitsConfig(
      process.env.REACT_APP_ENVIRONMENT !== "Production"
        ? "SoarD365Base"
        : "SoarLive"
    ).then((response) => {
      if (response && isMountedRef.current) {
        const unit = response.find((u) => u.ccx_unitid === programUnitId);
        if (unit) {
          setPuConfig(unit);
        }
      }
    });
  };

  useEffect(() => {
    if (id) {
      // getDivisionFosterHomes(divisions).then((response) => {
      //   if (isMountedRef.current && response) {
      //     const addressesResult = response.filter((r) =>
      //       getFieldText(r.ccx_parentid)
      //         .toUpperCase()
      //         .includes(id.toUpperCase())
      //     );
      //     if (addressesResult.length > 0) setAddresses(addressesResult);
      //   }
      // });
      getFosterHomeAddressesByID(id).then((response) => {
        if (isMountedRef.current && response) {
          if (response) setAddresses(response);
        }
      });
    }
  }, [id]);

  useEffect(() => {
    if (home.ccx_fostercarehomeid || updateMembers) {
      setUpdateMembers(false);
      getFosterHomeMembers(home.ccx_fostercarehomeid).then((response) => {
        if (response && isMountedRef.current) {
          setMembers(response);
        }
      });
    } else {
      setMembers([]);
    }
  }, [home, updateMembers]);

  const handleUpdateMembers = () => {
    setUpdateMembers(true);
  };

  const openInquiry = () => {
    const url = `${
      process.env.REACT_APP_ORG
    }/main.aspx?etc=10231&extraqs=formid%3d626dfe3b-0550-44e1-90ba-9004ebab4c31&id=${getFieldText(
      home.ccx_fosterparentinquiry
    )}&pagetype=entityrecord`;
    window.open(url, "inquiry");
  };

  // const addBed = () => {
  //   const url =
  //     process.env.REACT_APP_ORG +
  //     `/main.aspx?etc=10223&extraqs=%3f_CreateFromId%3d${getFieldText(
  //       home.ccx_parentid
  //     )}%26_CreateFromType%3d10221%26etc%3d10223&histKey=599033237&newWindow=true&pagetype=entityrecord`;
  //   window.open(url, "FosterHome");
  // };

  // const addInvestigation = () => {
  //   const url =
  //     process.env.REACT_APP_ORG +
  //     `/main.aspx?etc=10539&extraqs=%3f_CreateFromId%3d${getFieldText(
  //       home.ccx_parentid
  //     )}%26_CreateFromType%3d10221%26etc%3d10539&histKey=599033237&newWindow=true&pagetype=entityrecord`;
  //   window.open(url, "FosterHome");
  // };

  // const addCorrespondence = () => {
  //   const url =
  //     process.env.REACT_APP_ORG +
  //     `/main.aspx?etc=10514&extraqs=%3f_CreateFromId%3d${getFieldText(home.ccx_parentid)}%26_CreateFromType%3d10221%26etc%3d10514&histKey=599033237&newWindow=true&pagetype=entityrecord`;
  //   window.open(url, "FosterHome");
  // };

  // const notesToggle = useAccordionToggle("3", () =>
  // );

  const handleEditSageVendorId = () => {
    setEditingSageVendorId(true);
  };

  const handleChange = ({ currentTarget: input }) => {
    setSageVendorId(input.value);
  };

  const handleSave = async () => {
    setEditingSageVendorId(false);

    const saveObj = {
      sor_assettag: {
        type: "string",
        value: sageVendorId,
      },
    };

    const toastId = toast.loading("Updating Sage Vendor Id...", {
      type: toast.TYPE.INFO,
    });

    try {
      await updateEntity(
        "ccx_fostercarehome",
        home.ccx_fostercarehomeid,
        JSON.stringify(saveObj),
        safyInfo
      );
      toast.update(toastId, {
        render: "Sage Vendor Id has been updated.",
        type: toast.TYPE.SUCCESS,
        isLoading: false,
        autoClose: 5000,
        closeOnClick: true,
      });

      const obj = { ...home };
      obj.sor_assettag = sageVendorId;
      setHome(obj);
    } catch {
      toast.update(toastId, {
        render: "An error occurred while updating Sage Vendor Id.",
        type: toast.TYPE.ERROR,
        isLoading: false,
        autoClose: 5000,
        closeOnClick: true,
      });
    }
  };

  return (
    <>
      {!props.passedID && <BackIcon />}
      {/* <PrintIcon /> */}
      <div className="card card-safy mt-4">
        <div className="card-header bg-safy3-1">
          <div className="row pe-2">
            <div className="col-10">
              <div className="h4 mt-0 mb-0 fw-light text-light">
                {home.sor_fosterhomeid
                  ? home.sor_fosterhomeid + "-" + home.ccx_name
                  : home.ccx_name}
              </div>
            </div>
            <div className="col-2 p-0 d-flex justify-content-end">
              <i
                className="fas fa-pen float-end p-2 text-light"
                onClick={() => {
                  const url =
                    process.env.REACT_APP_ORG +
                    "/main.aspx?etc=10221&extraqs=formid%3da28fd99b-80b7-46a0-9dbc-e83d4d6dffda&id=" +
                    home.ccx_fostercarehomeid +
                    "&pagetype=entityrecord";
                  window.open(url, "Address");
                }}
              ></i>
            </div>
          </div>
        </div>
        <div className="card-body">
          <div className="row row-cols-1 row-cols-sm-2 ">
            <div className="col">
              {addresses &&
                addresses.length > 0 &&
                addresses.map((address, index) => (
                  <div key={`${address.ccx_contactaddressid}${index}`}>
                    <h6>
                      {address.ccx_line1}
                      <i
                        className="fa fa-map-pin text-danger ms-2"
                        onClick={() =>
                          window.open(
                            `https://maps.google.com/?q=${address.ccx_line1} ${
                              address.ccx_city
                            } ${getFieldName(address.ccx_state_lu)} ${
                              address.ccx_postalcode
                            }`
                          )
                        }
                      ></i>
                    </h6>
                    <h6>
                      {address.ccx_city && `${address.ccx_city}, `}
                      {address.ccx_state_lu &&
                        `${getFieldName(address.ccx_state_lu)} `}
                      {address.ccx_postalcode}
                    </h6>
                    {address.ccx_county_lu && (
                      <h6>{`${getFieldName(address.ccx_county_lu)} County`}</h6>
                    )}
                    {address.ccx_telephone1 && (
                      <h6>Phone: {address.ccx_telephone1}</h6>
                    )}
                    {address.ccx_telephone2 && (
                      <h6>Phone 2: {address.ccx_telephone2}</h6>
                    )}
                  </div>
                ))}
              {getFieldName(home.ccx_unitdetail)}
              {addresses.length > 0 &&
                getFieldName(addresses[0].ccx_state_lu) === "Ohio" && (
                  <div className="mt-4">
                    <FieldDisplay
                      label="CPR Expiration"
                      value={getFieldDate(home.sor_cprexpdate)}
                    />
                    <FieldDisplay
                      label="First Aid Expiration"
                      value={getFieldDate(home.sor_firstaidexpdate)}
                    />
                    <FieldDisplay
                      label="Ukeru Expiration"
                      value={getFieldDate(home.sor_ukeruexpdate)}
                    />
                  </div>
                )}
            </div>
            <div className="col">
              <FieldDisplay
                label="Licensed"
                value={getFieldDate(home.svc_licenseddate)}
              />
              <FieldDisplay
                label="Capacity"
                value={getFieldFormattedValue(home.ccx_capacity)}
              />
              {safyInfo.user.modules.includes("Admin") ||
              safyInfo.user.modules.includes("Rev Cycle") ? (
                <>
                  {editingSageVendorId ? (
                    <Input
                      name="sor_assettag"
                      label={
                        <>
                          Sage Vendor ID
                          <i
                            className="ms-2 mt-1 fas fa-save"
                            onClick={handleSave}
                          ></i>
                        </>
                      }
                      value={sageVendorId || home.sor_assettag}
                      onChange={handleChange}
                    />
                  ) : (
                    <FieldDisplay
                      label={
                        <>
                          Sage Vendor ID
                          <i
                            className={
                              "ms-2 mt-1 " +
                              (sageVendorId || home.sor_assettag
                                ? "fas fa-pen"
                                : "fas fa-plus-circle")
                            }
                            onClick={handleEditSageVendorId}
                          ></i>
                        </>
                      }
                      value={sageVendorId || home.sor_assettag}
                    />
                  )}
                </>
              ) : null}
              {home.ccx_fosterparentinquiry && (
                <button
                  className="btn btn-safy-info mt-4 me-2"
                  onClick={openInquiry}
                >
                  Open Foster Parent Inquiry
                </button>
              )}
            </div>
          </div>
          <div className="row mt-4">
            <div className="col">
              <Tabs
                activeKey={activeTab}
                id="fosterhome-tabs"
                className="cptabs mb-4"
                transition={false}
                onSelect={(key) => {
                  setActiveTab(key);

                  switch (key) {
                    case "beds":
                      loadFosterHomeBedsEntity();
                      break;
                    case "rates":
                      loadFosterHomeRatesEntity();
                      break;
                    case "rules":
                      loadRulesInvestigations();
                      break;
                    case "correspondence":
                      loadCorrespondence();
                      break;
                    case "notes":
                      loadFosterHomeNotes();
                      break;
                    default:
                      break;
                  }
                }}
              >
                <Tab
                  eventKey="members"
                  title="Foster Home Members"
                  className="cptabs"
                >
                  <FosterHomeMembersEntity
                    fosterHome={home}
                    members={members}
                    updateMembers={handleUpdateMembers}
                  />
                </Tab>
                <Tab
                  eventKey="beds"
                  title="Foster Home Beds"
                  className="cptabs"
                >
                  {fosterHomeBedsLazy}
                </Tab>
                <Tab
                  eventKey="rates"
                  title="Foster Home Rates"
                  className="cptabs"
                >
                  {fosterHomeRatesLazy}
                </Tab>
                <Tab
                  eventKey="rules"
                  title="Rules Investigation"
                  className="cptabs"
                >
                  {rulesInvestigationsLazy}
                </Tab>
                <Tab
                  eventKey="correspondence"
                  title="SOAR Correspondence"
                  className="cptabs"
                >
                  {correspondenceLazy}
                </Tab>
                <Tab eventKey="notes" title="Notes" className="cptabs">
                  {notesLazy}
                </Tab>

                <Tab eventKey="documents" title="Documents" className="cptabs">
                  {activeTab === "documents" ? (
                    <Documents
                      id={home.ccx_fostercarehomeid.replace(/[{}]/g, "")}
                      type="HomeID"
                      prevLocation={home.doclocation[0]}
                      state={home.owningbusinessunit["@name"].replace(
                        "SAFY of ",
                        ""
                      )}
                      puconfig={
                        puConfig?.sor_safyportalconfig
                          ? JSON.parse(puConfig.sor_safyportalconfig)
                          : {}
                      }
                    />
                  ) : null}
                </Tab>
              </Tabs>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default FosterHome;
